import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as authApi from '../api/authApi';
import { onUnauthorized } from '../lib/apiClient';
import { keys } from '../lib/queryKeys';
import { createSessionExpiryStore, resolveSessionCheck } from '../lib/sessionExpiry';
import { clearPersistedCampaignForm } from '../features/dashboard/campaignFormStorage';

/**
 * Session state.
 *
 * The session itself lives in an httpOnly cookie, which JavaScript cannot read by
 * design. So this does not track a token — it tracks the server's answer to "am I
 * signed in", via /check-auth, and treats that answer as authoritative.
 *
 * ── Expiry is not sign-out ───────────────────────────────────────────────────
 *
 * Any 401 anywhere in the app (via `onUnauthorized`), or a /check-auth that comes back
 * signed-out while the app believed it was signed in, is recorded as an *expiry*
 * (lib/sessionExpiry.js). The current screen stays mounted, untouched, behind a
 * blocking "session expired" dialog — so a half-composed campaign is not unmounted
 * out from under the operator. Only when they choose "Log In Again" does the app
 * clear the session and route to the login screen.
 *
 * That path deliberately does NOT clear the per-tab campaign draft, unlike an
 * explicit logout: the draft is stamped with the operator's email, so it is restored
 * if the same operator signs back in and discarded if anyone else does.
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const [expiryStore] = useState(createSessionExpiryStore);
  const sessionExpired = useSyncExternalStore(expiryStore.subscribe, expiryStore.getSnapshot);

  /** Why the operator was last sent to the login screen: 'expired' or null. */
  const [signOutReason, setSignOutReason] = useState(null);

  const reportExpired = useCallback(() => {
    const current = queryClient.getQueryData(keys.auth.session);
    expiryStore.report({ authenticated: Boolean(current?.authenticated) });
  }, [expiryStore, queryClient]);

  const sessionQuery = useQuery({
    queryKey: keys.auth.session,
    queryFn: async ({ signal }) => {
      const result = await authApi.checkAuth(signal);

      const { data, action } = resolveSessionCheck({
        previous: queryClient.getQueryData(keys.auth.session),
        result,
        expired: expiryStore.getSnapshot(),
        suppressed: expiryStore.isSuppressed()
      });

      if (action === 'expire') reportExpired();

      if (action === 'recover') {
        // The same operator signed back in from another tab. Close the dialog and
        // refetch whatever failed while the session was gone.
        expiryStore.clear();
        queueMicrotask(() =>
          queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] !== 'auth' })
        );
      }

      return data;
    },
    staleTime: 60_000,
    // A session can expire while the tab sits in the background, so re-checking on
    // focus means the operator is told before their next action fails.
    refetchOnWindowFocus: true,
    retry: 1
  });

  /**
   * Drops all cached server state.
   *
   * Not merely cosmetic: the cache holds the previous operator's SMTP hostnames,
   * recipient files, campaign logs and IMAP accounts. Leaving it in place would show
   * one operator another's data for as long as it took each panel to refetch.
   */
  const resetSession = useCallback(() => {
    queryClient.setQueryData(keys.auth.session, { authenticated: false });
    queryClient.removeQueries({ queryKey: keys.files.all });
    queryClient.removeQueries({ queryKey: keys.imap.all });
    queryClient.removeQueries({ queryKey: keys.emailConfig });
    queryClient.removeQueries({ queryKey: keys.health });
  }, [queryClient]);

  // One subscription for the whole app: every 401 from any endpoint lands here. The
  // store collapses a burst of simultaneous 401s into a single expiry.
  useEffect(() => onUnauthorized(reportExpired), [reportExpired]);

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (data) => {
      expiryStore.clear();
      setSignOutReason(null);
      queryClient.setQueryData(keys.auth.session, { authenticated: true, user: data?.user });
    }
  });

  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    // A /check-auth or 401 landing mid-logout is the logout itself, not an expiry.
    onMutate: () => expiryStore.setSuppressed(true),
    // onSettled rather than onSuccess: if the request fails, the operator still asked
    // to leave, and the cookie may well be gone anyway. Staying signed in after a
    // failed logout is the worse outcome.
    onSettled: () => {
      // The campaign draft is per-tab sessionStorage stamped with the operator's
      // email, so it survives a logout in this tab. Cleared here so the next person
      // to sign in does not inherit the previous operator's SMTP settings.
      clearPersistedCampaignForm();
      setSignOutReason(null);
      resetSession();
      expiryStore.clear();
      expiryStore.setSuppressed(false);
    }
  });

  /**
   * "Log In Again" from the session-expired dialog.
   *
   * Asks the server to clear the (already invalid) cookie — /logout is mounted ahead
   * of the auth middleware, so it works with a dead session — then drops cached
   * server state. RequireAuth sees the signed-out state and routes to the login
   * screen, carrying the current location so the operator comes back to it.
   *
   * The campaign draft is kept on purpose; see the module comment.
   */
  const reauthMutation = useMutation({
    mutationFn: authApi.logout,
    onMutate: () => expiryStore.setSuppressed(true),
    onSettled: () => {
      setSignOutReason('expired');
      resetSession();
      expiryStore.clear();
      expiryStore.setSuppressed(false);
    }
  });

  const { mutateAsync: reauthAsync } = reauthMutation;
  const reauthenticate = useCallback(async () => {
    try {
      await reauthAsync();
    } catch {
      // Unreachable server: the cookie is already rejected server-side, so the local
      // reset in onSettled is all that matters and has already happened.
    }
  }, [reauthAsync]);

  const value = useMemo(
    () => ({
      user: sessionQuery.data?.authenticated ? (sessionQuery.data.user ?? null) : null,
      isAuthenticated: Boolean(sessionQuery.data?.authenticated),
      // Only the very first read is "loading". A background revalidation must not
      // blank the app, which is what gating on isFetching would do.
      isLoading: sessionQuery.isLoading,
      isError: sessionQuery.isError,
      refresh: sessionQuery.refetch,

      sessionExpired,
      signOutReason,
      reauthenticate,
      isReauthenticating: reauthMutation.isPending,

      login: loginMutation.mutateAsync,
      isLoggingIn: loginMutation.isPending,

      logout: logoutMutation.mutateAsync,
      isLoggingOut: logoutMutation.isPending
    }),
    [
      sessionQuery.data,
      sessionQuery.isLoading,
      sessionQuery.isError,
      sessionQuery.refetch,
      sessionExpired,
      signOutReason,
      reauthenticate,
      reauthMutation.isPending,
      loginMutation.mutateAsync,
      loginMutation.isPending,
      logoutMutation.mutateAsync,
      logoutMutation.isPending
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider.');
  return context;
}
